import React, { useMemo } from 'react'

import { styled, Typography } from '@mui/material'

const Title = styled(Typography)(({ theme }) => ({
  margin: '10px 0px',
  ...theme.typography.h4,
}))

interface PaginationCounterProps {
  count: number
  total?: number
  currentPage: number
  itemsPerPage: number
  label: string
  'data-testid': string
}

/**
 * A component that displays pagination information about the current range of items being shown.
 *
 * @component
 * @example
 * // Example usage:
 * <PaginationCounter
 *   label="products"      // `Showing ${X} of ${Y} ${label}.`
 *   count={10}            // Number of items currently displayed
 *   total={100}           // Total number of items available
 *   currentPage={2}       // Current page number
 *   itemsPerPage={20}     // Number of items per page
 *   data-testid={TestIds.products.paginationCounter} // What data-testid to use
 * />
 *
 * @param {Object} props - The props for the PaginationCounter component.
 * @param {string} props.label - The label for the items being displayed (e.g., "products").
 * @param {number} props.count - The number of items currently displayed.
 * @param {number} props.total - The total number of items available.
 * @param {number} props.currentPage - The current page number.
 * @param {number} props.itemsPerPage - The number of items per page.
 * @param {number} props["data-testid"] - What data-testid to use
 * @returns {JSX.Element} The PaginationCounter component.
 */
export const PaginationCounter: React.FC<PaginationCounterProps> = ({
  label,
  count,
  total,
  currentPage,
  itemsPerPage,
  ...props
}) => {
  /**
   * A memoized function that calculates the title to display based on the current pagination state.
   *
   * @returns {string} The formatted title based on the pagination state.
   */
  const renderTitle = useMemo(() => {
    if (!total) {
      return `Showing ${count} ${label}.`
    }
    const startIndex = (currentPage - 1) * itemsPerPage + 1
    const endIndex = Math.min(currentPage * itemsPerPage, total)
    if (count === 0) return `Showing 0 ${label}.`
    if (total <= itemsPerPage) return `Showing ${count} of ${total} ${label}.`
    return `Showing ${startIndex} - ${endIndex} of ${total} ${label}.`
  }, [count, total, currentPage, label, itemsPerPage])

  return (
    <Title variant='h2' data-testid={props['data-testid']}>
      {renderTitle}
    </Title>
  )
}

export default PaginationCounter
